import React, { useEffect, useState, useCallback } from 'react';
import { getRequests, approveRequest, denyRequest, AccessRequest } from '../api';
import AdminSettings from './AdminSettings';

type Tab = 'pending' | 'approved' | 'denied' | 'settings';

const AdminDashboard: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pending');
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [modalAction, setModalAction] = useState<'approve' | 'deny' | null>(null);
  const [modalRequestId, setModalRequestId] = useState('');
  const [modalNote, setModalNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadRequests = useCallback(async (status: Tab) => {
    setLoading(true);
    setError('');
    try {
      const data = await getRequests(status);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'settings') {
      loadRequests(tab);
    }
  }, [tab, loadRequests]);

  const openModal = (action: 'approve' | 'deny', requestId: string) => {
    setModalAction(action);
    setModalRequestId(requestId);
    setModalNote('');
  };

  const closeModal = () => {
    setModalAction(null);
    setModalRequestId('');
    setModalNote('');
  };

  const handleAction = async () => {
    if (!modalAction || !modalRequestId) return;
    setActionLoading(true);
    try {
      if (modalAction === 'approve') {
        await approveRequest(modalRequestId, modalNote);
      } else {
        await denyRequest(modalRequestId, modalNote);
      }
      closeModal();
      await loadRequests(tab);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const tabs: Tab[] = ['pending', 'approved', 'denied', 'settings'];

  return (
    <div>
      <h1 style={styles.heading}>Admin Dashboard</h1>
      <p style={styles.description}>
        Review and manage MCP access requests.
      </p>

      <div style={styles.tabBar}>
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              ...styles.tabBtn,
              ...(tab === t ? styles.tabActive : {}),
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'settings' ? (
        <AdminSettings />
      ) : (
      <>
      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <p style={styles.loading}>Loading requests...</p>
      ) : requests.length === 0 ? (
        <div style={styles.empty}>
          <p>No {tab} requests found.</p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Requester</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Scope</th>
                <th style={styles.th}>Reason</th>
                {tab !== 'pending' && <th style={styles.th}>Note</th>}
                {tab === 'pending' && <th style={styles.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} style={styles.tr}>
                  <td style={styles.td}>
                    {new Date(req.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>{req.userName}</td>
                  <td style={styles.td}>{req.userEmail}</td>
                  <td style={styles.td}>
                    <span style={styles.scope}>{req.scope}</span>
                  </td>
                  <td style={{ ...styles.td, maxWidth: 260 }}>
                    {req.reason.length > 100
                      ? req.reason.substring(0, 100) + '...'
                      : req.reason}
                  </td>
                  {tab !== 'pending' && (
                    <td style={styles.td}>{req.adminNote || '--'}</td>
                  )}
                  {tab === 'pending' && (
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          onClick={() => openModal('approve', req.id)}
                          style={styles.approveBtn}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openModal('deny', req.id)}
                          style={styles.denyBtn}
                        >
                          Deny
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Action Modal */}
      {modalAction && (
        <div style={styles.overlay} onClick={closeModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {modalAction === 'approve' ? 'Approve' : 'Deny'} Request
            </h2>
            <p style={styles.modalText}>
              {modalAction === 'approve'
                ? 'Confirm approval of this access request. Optionally add a note.'
                : 'Confirm denial of this access request. Optionally provide a reason.'}
            </p>
            <textarea
              value={modalNote}
              onChange={(e) => setModalNote(e.target.value)}
              placeholder="Optional note..."
              rows={3}
              style={styles.modalTextarea}
            />
            <div style={styles.modalActions}>
              <button onClick={closeModal} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={actionLoading}
                style={
                  modalAction === 'approve'
                    ? styles.confirmApproveBtn
                    : styles.confirmDenyBtn
                }
              >
                {actionLoading
                  ? 'Processing...'
                  : modalAction === 'approve'
                    ? 'Approve'
                    : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  heading: {
    fontSize: '1.6rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  description: {
    color: '#666',
    marginBottom: '1.5rem',
  },
  tabBar: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  tabBtn: {
    padding: '0.5rem 1.25rem',
    border: '1px solid #ddd',
    background: '#fff',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#555',
  },
  tabActive: {
    background: '#4a6cf7',
    color: '#fff',
    borderColor: '#4a6cf7',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#888',
  },
  errorBanner: {
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: 6,
    padding: '0.85rem 1rem',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    color: '#888',
  },
  tableWrapper: {
    overflowX: 'auto',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem 1rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '2px solid #eee',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    verticalAlign: 'top',
  },
  scope: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  approveBtn: {
    background: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '0.35rem 0.85rem',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  denyBtn: {
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '0.35rem 0.85rem',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  // Modal styles
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff',
    borderRadius: 10,
    padding: '2rem',
    maxWidth: 460,
    width: '90%',
    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
  },
  modalTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    marginBottom: '0.5rem',
  },
  modalText: {
    color: '#555',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    lineHeight: 1.5,
  },
  modalTextarea: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: '0.9rem',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    marginBottom: '1rem',
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  },
  cancelBtn: {
    background: '#f0f0f0',
    color: '#333',
    border: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  confirmApproveBtn: {
    background: '#28a745',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  confirmDenyBtn: {
    background: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
};

export default AdminDashboard;
